/**
 * Authentication API
 * 
 * Provides methods for authentication-related API calls.
 * All methods use the centralized axios instance with automatic token handling.
 * 
 * @module authApi
 */

import api from './axiosConfig';

/**
 * Login API call
 * Authenticates a user with username/email and password
 * 
 * @param {string} usernameOrEmail - Username or email address
 * @param {string} password - User password
 * @returns {Promise<Object>} Response containing token and user data
 * @throws {Error} If login fails
 * 
 * @example
 * const response = await loginApi('user@example.com', 'password123');
 * console.log(response.token); // JWT token
 * console.log(response.user); // User data
 */
export const loginApi = async (usernameOrEmail, password) => {
  const response = await api.post('/api/auth/login', {
    usernameOrEmail,
    password
  });
  return response.data;
};

/**
 * Register API call
 * Creates a new user account
 * 
 * @param {Object} userData - User registration data
 * @param {string} userData.username - Username
 * @param {string} userData.email - Email address
 * @param {string} userData.password - Password
 * @param {string} userData.firstName - First name
 * @param {string} userData.lastName - Last name
 * @param {string} userData.userType - User type (STUDENT, TEACHER, PARENT, ADMIN, OWNER)
 * @param {string} [userData.institution] - Institution name (deprecated, use institutionId)
 * @param {string} [userData.institutionId] - Institution ID
 * @returns {Promise<Object>} Response containing token and user data
 * @throws {Error} If registration fails
 * 
 * @example
 * const response = await registerApi({
 *   username: 'johndoe',
 *   email: 'john@example.com',
 *   password: 'password123',
 *   firstName: 'John',
 *   lastName: 'Doe',
 *   userType: 'STUDENT',
 *   institutionId: '12345'
 * });
 */
export const registerApi = async (userData) => {
  const response = await api.post('/api/auth/register', userData);
  return response.data;
};

/**
 * Verify email API call
 * Verifies a user's email address using a verification token
 * 
 * @param {string} token - Email verification token
 * @returns {Promise<Object>} Response with verification status and message
 * @throws {Error} If verification fails
 * 
 * @example
 * const response = await verifyEmailApi('verification-token-here');
 * console.log(response.message); // 'Email verified successfully!'
 */
export const verifyEmailApi = async (token) => {
  const response = await api.get(`/api/auth/verify-email?token=${token}`);
  return response.data;
};

/**
 * Resend verification email API call
 * Requests a new verification email to be sent to the user
 * 
 * @param {string} email - User's email address
 * @returns {Promise<Object>} Response with message
 * @throws {Error} If resend fails
 * 
 * @example
 * const response = await resendVerificationEmailApi('user@example.com');
 * console.log(response.message); // 'Verification email sent successfully'
 */
export const resendVerificationEmailApi = async (email) => {
  const response = await api.post('/api/auth/resend-verification', { email });
  return response.data;
};

/**
 * Refresh token API call
 * Refreshes the authentication token
 * Note: This is a placeholder for future implementation
 * 
 * @returns {Promise<Object>} Response containing new token
 * @throws {Error} If refresh fails
 * 
 * @example
 * const response = await refreshTokenApi();
 * console.log(response.token); // New JWT token
 */
export const refreshTokenApi = async () => {
  const response = await api.post('/api/auth/refresh-token');
  return response.data;
};

